import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { profileAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, LogOut, User, Mail, Calendar, Shield, Pencil, Check, X, Lock, KeyRound, Loader2, AlertTriangle, Pause } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, logout, refreshUser, updateUserLocally } = useAuth();
  const navigate = useNavigate();

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  // Email change
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailStep, setEmailStep] = useState('password'); // password, verify
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Password change
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Cancel subscription
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  const handleSaveName = async () => {
    if (!newName.trim()) { toast.error('Name cannot be empty'); return; }
    setSavingName(true);
    try {
      const res = await profileAPI.update({ name: newName.trim() });
      updateUserLocally({ name: newName.trim() });
      setEditingName(false);
      toast.success('Name updated');
    } catch (err) {
      toast.error('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleEmailChangeStart = async () => {
    if (!newEmail.trim()) { toast.error('Enter a new email'); return; }
    if (!emailPassword) { toast.error('Enter your password to confirm'); return; }
    setSavingEmail(true);
    try {
      await profileAPI.changeEmail({ new_email: newEmail, password: emailPassword });
      toast.success('Verification code sent to new email');
      setEmailStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate email change');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleEmailVerify = async () => {
    setSavingEmail(true);
    try {
      const res = await profileAPI.verifyEmailChange({ code: emailCode });
      if (res.data.token) {
        localStorage.setItem('brandos_token', res.data.token);
      }
      updateUserLocally({ email: res.data.new_email });
      setEditingEmail(false);
      setEmailStep('password');
      setNewEmail('');
      setEmailPassword('');
      setEmailCode('');
      toast.success('Email updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setSavingPassword(true);
    try {
      await profileAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      setEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handlePauseSubscription = async () => {
    setCancelling(true);
    try {
      await profileAPI.cancelSubscription({ pause_months: 3 });
      toast.success('Subscription paused for 3 months');
      setShowCancelModal(false);
      refreshUser();
    } catch (err) {
      toast.error('Failed to pause subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await profileAPI.cancelSubscription({});
      toast.success('Subscription cancelled');
      setShowCancelModal(false);
      refreshUser();
    } catch (err) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-lg mx-auto px-6 h-16 flex items-center">
          <button data-testid="profile-back-btn" onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600" style={{ transition: 'color 0.2s' }}>
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-6">
        <h1 className="text-3xl font-bold tracking-tight mb-8 animate-fade-in-up" data-testid="profile-title">Profile</h1>

        {/* Avatar & Name */}
        <div className="flex items-center gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
            <User className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input data-testid="profile-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} className="h-10 bg-gray-50 border-gray-200 rounded-xl text-base" autoFocus />
                <button data-testid="profile-save-name-btn" onClick={handleSaveName} disabled={savingName} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" style={{ transition: 'background-color 0.2s' }}>
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button onClick={() => { setEditingName(false); setNewName(user?.name || ''); }} className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg" style={{ transition: 'background-color 0.2s' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight">{user?.name || 'User'}</h2>
                <button data-testid="profile-edit-name-btn" onClick={() => setEditingName(true)} className="p-1.5 text-gray-300 hover:text-purple-600 rounded-lg" style={{ transition: 'color 0.2s' }}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <p className="text-sm text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Info Cards */}
        <div className="space-y-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <InfoRow icon={<Calendar className="w-4 h-4" strokeWidth={1.5} />} label="Joined" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'} />
          <InfoRow icon={<Shield className="w-4 h-4" strokeWidth={1.5} />} label="Auth" value={user?.auth_provider === 'firebase' ? 'Google (Firebase)' : 'Email & Password'} />
          <InfoRow icon={<Mail className="w-4 h-4" strokeWidth={1.5} />} label="Verified" value={user?.email_verified ? 'Yes' : 'No'} />
        </div>

        {/* Change Email Section */}
        {user?.auth_provider === 'email' && (
          <div className="mb-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            {!editingEmail ? (
              <button data-testid="profile-change-email-btn" onClick={() => setEditingEmail(true)} className="w-full flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 text-left" style={{ transition: 'background-color 0.2s' }}>
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Change Email</p>
                  <p className="text-xs text-gray-400">Update your email address</p>
                </div>
              </button>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Change Email</p>
                  <button onClick={() => { setEditingEmail(false); setEmailStep('password'); setNewEmail(''); setEmailPassword(''); setEmailCode(''); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {emailStep === 'password' && (
                  <>
                    <Input data-testid="profile-new-email-input" type="email" placeholder="New email address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-11 bg-white border-gray-200 rounded-xl text-sm" />
                    <Input data-testid="profile-email-password-input" type="password" placeholder="Confirm your password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className="h-11 bg-white border-gray-200 rounded-xl text-sm" />
                    <Button data-testid="profile-send-email-code-btn" onClick={handleEmailChangeStart} disabled={savingEmail} className="w-full rounded-xl h-11 bg-[#09090b] text-white text-sm">
                      {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Verification Code'}
                    </Button>
                  </>
                )}
                {emailStep === 'verify' && (
                  <>
                    <p className="text-xs text-gray-400">Enter the code sent to {newEmail}</p>
                    <Input data-testid="profile-email-code-input" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" maxLength={6} className="h-11 bg-white border-gray-200 rounded-xl text-sm text-center tracking-[0.5em] font-mono" />
                    <Button data-testid="profile-verify-email-btn" onClick={handleEmailVerify} disabled={savingEmail || emailCode.length !== 6} className="w-full rounded-xl h-11 bg-[#09090b] text-white text-sm">
                      {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Update'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Change Password Section */}
        {user?.auth_provider === 'email' && (
          <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {!editingPassword ? (
              <button data-testid="profile-change-password-btn" onClick={() => setEditingPassword(true)} className="w-full flex items-center gap-3 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 text-left" style={{ transition: 'background-color 0.2s' }}>
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                  <Lock className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Change Password</p>
                  <p className="text-xs text-gray-400">Update your account password</p>
                </div>
              </button>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Change Password</p>
                  <button onClick={() => { setEditingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <Input data-testid="profile-current-password-input" type="password" placeholder="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11 bg-white border-gray-200 rounded-xl text-sm" />
                <Input data-testid="profile-new-password-input" type="password" placeholder="New password (min 6 chars)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-11 bg-white border-gray-200 rounded-xl text-sm" />
                <Input data-testid="profile-confirm-password-input" type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="h-11 bg-white border-gray-200 rounded-xl text-sm" />
                <Button data-testid="profile-save-password-btn" onClick={handlePasswordChange} disabled={savingPassword} className="w-full rounded-xl h-11 bg-[#09090b] text-white text-sm">
                  {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Cancel Subscription - hidden when app is free for everyone */}
        {false && user?.subscription_status === 'active' && user?.subscription_plan !== 'admin' && (
          <button
            data-testid="cancel-subscription-btn"
            onClick={() => setShowCancelModal(true)}
            className="w-full text-center text-xs text-gray-300 hover:text-gray-400 py-3 mb-2"
            style={{ transition: 'color 0.2s' }}
          >
            Cancel my subscription
          </button>
        )}

        {/* Logout */}
        <Button data-testid="logout-btn" onClick={handleLogout} variant="outline" className="w-full rounded-full h-14 border-red-200 text-red-500 hover:bg-red-50 font-medium text-base active:scale-[0.98]" style={{ transition: 'background-color 0.2s, transform 0.15s' }}>
          <LogOut className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Sign Out
        </Button>
      </div>

      {/* Cancel/Pause Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cancel-modal" onClick={() => setShowCancelModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-8 animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-bold mb-1">We're sorry to see you go</h2>
              <p className="text-gray-400 text-sm">Your progress and data will be saved if you decide to come back.</p>
            </div>

            <Button
              data-testid="pause-subscription-btn"
              onClick={handlePauseSubscription}
              disabled={cancelling}
              className="w-full h-14 rounded-xl bg-purple-600 text-white hover:bg-purple-500 font-semibold text-base mb-3 shadow-lg shadow-purple-500/20"
            >
              {cancelling ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <Pause className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Pause for 3 months (no payment)
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                data-testid="confirm-cancel-btn"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="text-sm text-gray-400 hover:text-gray-600"
                style={{ transition: 'color 0.2s' }}
              >
                Cancel anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4" data-testid={`profile-info-${label.toLowerCase()}`}>
      <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-700">{value || '-'}</p>
      </div>
    </div>
  );
}
